import logo from '../../src/Assets/newlogin.jpg';
import React, {useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {LogIn, User, Lock, AlertTriangle, Loader2, BookOpen} from 'lucide-react';

const LoginPage = () => {
    const [credentials, setCredentials] = useState({
        username: '', // Changed from email to username to match backend expectations
        password: ''
    });
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();

    const handleChange = (e) => {
        const {name, value} = e.target;
        setCredentials(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Initialize chatbot function (after successful login)
    const initializeChatbot = async () => {
        try {
            const accessToken = localStorage.getItem('accessToken');

            if (!accessToken) {
                throw new Error("No access token found. Please login or sign up.");
            }

            const initResponse = await fetch('https://api.digitalaalim.com/initialize_model/', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Origin': 'https://chat.digitalaalim.in/'
                },
            });

            if (!initResponse.ok) {
                if (initResponse.statusText === "Unauthorized") {
                    localStorage.removeItem('accessToken');
                    navigate('/');
                    throw new Error(`Session Expired. Login again`);
                } else {
                    throw new Error(`Failed to initialize the model: ${initResponse.statusText}`);
                }
            }

            // If initialization is successful, continue to dashboard
            navigate('/dashboard');
        } catch (error) {
            console.error('Error initializing the chatbot:', error.message);
            setError(`Initialization failed: ${error.message}`);
            setIsLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);

        try {
            // Using URLSearchParams as in the working implementation
            const formData = new URLSearchParams();
            formData.append('username', credentials.username);
            formData.append('password', credentials.password);

            const response = await fetch('https://api.digitalaalim.com/login/admin', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                    'Origin': 'https://chat.digitalaalim.in/'
                },
                body: formData
            });

            if (!response.ok) {
                throw new Error('Login failed. Please check your credentials.');
            }

            const data = await response.json();

            // Store token in localStorage
            localStorage.setItem('accessToken', data.access_token);

            // Initialize chatbot after successful login
            await initializeChatbot();
        } catch (error) {
            console.error('Login error:', error);
            setError(error.message || 'An error occurred during login');
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
            <div className="w-full max-w-5xl flex overflow-hidden rounded-2xl shadow-xl">
                {/* Left side - Logo and branding */}
                <div
                    className="w-full md:w-1/2 bg-gradient-to-b from-[#00BCD4] to-[#FF7043] flex flex-col items-center justify-center p-12 text-white">
                    <div className="flex flex-col items-center">
                        <img
                            src={logo}
                            alt="Digital-Aalim-Logo"
                            className="w-full max-w-xs"
                        />
                        <h2 className="mt-8 text-2xl font-bold text-white text-center">Islamic Content Management</h2>
                        <p className="mt-4 text-white/80 text-center max-w-xs">
                            Your ultimate solution for Islamic text retrieval and chatbot training
                        </p>
                        <div className="mt-12 flex items-center justify-center space-x-3">
                            <BookOpen className="h-6 w-6"/>
                            <span className="text-lg font-medium">Digital Aalim Admin Panel</span>
                        </div>
                    </div>
                </div>

                {/* Right side - Login form */}
                <div className="w-full md:w-1/2 bg-white p-12 flex flex-col justify-center">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-800 mb-2">Welcome Back</h1>
                        <p className="text-gray-600 mb-8">Sign in to your admin account</p>

                        {error && (
                            <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 rounded">
                                <div className="flex items-center">
                                    <AlertTriangle className="h-5 w-5 mr-2 flex-shrink-0"/>
                                    <p className="text-sm">{error}</p>
                                </div>
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div className="space-y-2">
                                <label htmlFor="username" className="block text-sm font-medium text-gray-700">
                                    Email Address
                                </label>
                                <div className="relative rounded-md shadow-sm">
                                    <div
                                        className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <User className="h-5 w-5 text-gray-400"/>
                                    </div>
                                    <input
                                        id="username"
                                        name="username"
                                        type="email"
                                        autoComplete="email"
                                        required
                                        value={credentials.username}
                                        onChange={handleChange}
                                        className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#00BCD4] focus:border-[#00BCD4] text-sm"
                                        placeholder="admin@example.com"
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                                    Password
                                </label>
                                <div className="relative rounded-md shadow-sm">
                                    <div
                                        className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <Lock className="h-5 w-5 text-gray-400"/>
                                    </div>
                                    <input
                                        id="password"
                                        name="password"
                                        type="password"
                                        autoComplete="current-password"
                                        required
                                        value={credentials.password}
                                        onChange={handleChange}
                                        className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#00BCD4] focus:border-[#00BCD4] text-sm"
                                        placeholder="••••••••"
                                    />
                                </div>
                            </div>

                            <div>
                                <button
                                    type="submit"
                                    disabled={isLoading}
                                    className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#00BCD4] hover:[#00ACC1] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#00BCD4] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    {isLoading ? (
                                        <>
                                            <Loader2 className="animate-spin mr-2 h-4 w-4"/>
                                            Signing in...
                                        </>
                                    ) : (
                                        <>
                                            <LogIn className="mr-2 h-4 w-4"/>
                                            Sign in
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>

                        <div className="mt-12 pt-6 border-t border-gray-200">
                            <p className="text-center text-sm text-gray-600">
                                © {new Date().getFullYear()} Digital Aalim • Islamic Content Management
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LoginPage;